import { AdditiveBlending, BackSide, Color, Vector2 } from 'three';

import { CustomMaterial } from '@resn/gozer-three/materials';

import glslPaletteLerp from '@/libs/lygia/color/palette/lerp.glsl';

export const createTunnelMaterial = () => {
    const material = new CustomMaterial({
        uniforms: {
            uTime: 0,
            uNoiseMap: null,
            uNoiseScale1: 1,
            uNoiseScale2: 1,
            uNoiseScale3: 1,
            uNoiseSpeed: 0,
            uNoiseSpeed3: 0,
            uInProgress: 0,

            // Bend controls
            uBendAngle: 0.0, // radians, total bend applied from top->bottom
            uBendHeight: 0.0, // world units, cylinder height to derive radius = height / angle
            uBendDirection: new Vector2(1, 0), // xz direction the tunnel bends toward (normalized)

            uColor1: new Color(),
            uColor2: new Color(),
            uColorBrightness: 5.0,
        },

        vs: /*glsl*/ `
            uniform float uBendAngle;
            uniform float uBendHeight;
            uniform vec2 uBendDirection; // xz plane direction

            varying vec2 vUv;

            void main() {
                vUv = uv;
                vec3 pos = position;

                // Weight bend so top (vUv.y=1.0) is unchanged, bottom (vUv.y=0.0) is max
                float t = 1.0 - clamp(vUv.y, 0.0, 1.0);

                if (uBendAngle != 0.0 && uBendHeight != 0.0) {
                    // Bend plane direction in XZ
                    vec2 dir2 = normalize(uBendDirection);
                    vec3 dir3 = vec3(dir2.x, 0.0, dir2.y);

                    // Rotation axis is perpendicular to plane spanned by Y and dir3
                    vec3 rotAxis = normalize(cross(vec3(0.0, 1.0, 0.0), dir3));

                    // Derive circular arc radius from height and angle: R = H / |angle| (with epsilon)
                    float eps = 1e-6;
                    float angAbs = max(abs(uBendAngle), eps);
                    float R = uBendHeight / angAbs;
                    // Pivot is at +R along bend direction
                    vec3 pivot = dir3 * R;

                    // Angle applied for this vertex along the height; negate so positive bends toward dir
                    float theta = -uBendAngle * t;

                    // Rodrigues' rotation around arbitrary axis through origin
                    vec3 q = pos - pivot;
                    float ct = cos(theta);
                    float st = sin(theta);
                    vec3 vpar = rotAxis * dot(rotAxis, q);
                    vec3 vperp = q - vpar;
                    vec3 vperpRot = vperp * ct + cross(rotAxis, vperp) * st;
                    pos = vpar + vperpRot + pivot;
                }

                gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
            }
        `,
        fs: /*glsl*/ `
            uniform float uTime;
            uniform sampler2D uNoiseMap;
            uniform float uNoiseScale1;
            uniform float uNoiseScale2;
            uniform float uNoiseScale3;
            uniform float uNoiseSpeed;
            uniform float uNoiseSpeed3;
            uniform float uInProgress;
            uniform float uColorBrightness;

            uniform vec3 uColor1;
            uniform vec3 uColor2;

            varying vec2 vUv;

            ${glslPaletteLerp}

            float noiseFn(float offset) {
                float noiseSpeed = uTime * uNoiseSpeed;
                float noiseSpeed3 = uTime * uNoiseSpeed3;
                float xSpin = offset;

                vec2 noiseUv1 = vec2(vUv.x * uNoiseScale1 + offset - noiseSpeed, 0.0);
                vec2 noiseUv2 = vec2(vUv.x * uNoiseScale2 + offset + noiseSpeed, 0.0);
                vec2 noiseUv3 = vec2(vUv.x * uNoiseScale3 + offset, vUv.y * uNoiseScale3 + noiseSpeed3);

                float noise1 = texture2D(uNoiseMap, noiseUv1).r;
                float noise2 = texture2D(uNoiseMap, noiseUv2).r;
                float noise3 = texture2D(uNoiseMap, noiseUv3).r;

                float yNoise = mix(0.3, 1.0, smoothstep(0.0, 0.5, noise3));

                float noise = noise1 * noise2 * yNoise;
                float noiseFinal = smoothstep(0.0, 0.3, noise);

                return noiseFinal * uColorBrightness;
            }

            void main() {
                float finalNoise1 = noiseFn(0.0);
                float finalNoise2 = noiseFn(0.5);
                vec3 black = vec3(0.0);
                vec3 white = vec3(1.0);

                vec3 colorPallete1[3];
                colorPallete1[0] = black;
                colorPallete1[1] = uColor1;
                colorPallete1[2] = white;

                vec3 colorPallete2[3];
                colorPallete2[0] = black;
                colorPallete2[1] = uColor2;
                colorPallete2[2] = white;

                vec3 color1 = paletteLerp(colorPallete1, finalNoise1);
                vec3 color2 = paletteLerp(colorPallete2, finalNoise2);

                vec3 finalColor = color1 + color2;

                float featherEdge = smoothstep(0.0, 0.5, vUv.y) * smoothstep(1.0, 0.8, vUv.y);
                // float fadeIn = smoothstep(1.0, 0.0, vUv.y - mix(-1.0, 0.0, ));
                float fadeIn = smoothstep(0.0, 0.3, vUv.y + mix(-0.5, 0.0, uInProgress));

                // vec3 color = mix(uColors[0], uColors[1], noise);
                // color = mix(color, uColors[2], noise);

                float alpha = smoothstep(0.0, 0.2, uInProgress);
                alpha *= featherEdge * fadeIn;

                gl_FragColor = vec4(vec3(finalColor), alpha);
                // gl_FragColor = vec4(vec3(fadeIn), 1.0);
                // gl_FragColor = vec4(vec3(noise), 1.0);
                // gl_FragColor = vec4(vec3(1.0,0.0,0.0), 1.0);
            }
        `,
        options: {
            name: 'GrailTunnel',
            side: BackSide,
            // depthTest: false,
            // depthWrite: false,
            // blending: AdditiveBlending,
            defines: {
                PALETTE_LERP_SIZE: 3,
            },
        },
    });

    return material;
};
